/** CTL / ATL lines with TSB bars around zero, one point per day. */
import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { FitnessPoint } from '../../types/api.ts';
import { formatSigned } from '../../lib/chartData.ts';
import { formatDayLong, formatDayShort } from '../../lib/chartFormat.ts';
import { Legend, LegendItem, TooltipBox, TooltipRow } from './ChartParts.tsx';

const ATL_DASH = '5 3';

/** TSB split into a positive and a negative series so each gets its colour. */
type TrendRow = FitnessPoint & { tsbUp: number | null; tsbDown: number | null };

function trendRows(points: FitnessPoint[]): TrendRow[] {
  return points.map((p) => ({
    ...p,
    tsbUp: p.tsb >= 0 ? p.tsb : null,
    tsbDown: p.tsb < 0 ? p.tsb : null,
  }));
}

export function FitnessTrendChart({
  points,
  summary,
}: Readonly<{ points: FitnessPoint[]; summary: string }>) {
  const { t, i18n } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <div role="img" aria-label={summary} style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={trendRows(points)} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(d: string) => formatDayShort(d, i18n.language)}
              stroke="var(--chart-axis)"
              fontSize={11}
              minTickGap={24}
            />
            <YAxis stroke="var(--chart-axis)" fontSize={11} width={40} />
            <ReferenceLine y={0} stroke="var(--chart-axis)" />
            <Tooltip content={<FitnessTooltip />} />
            <Bar
              dataKey="tsbUp"
              stackId="tsb"
              name={t('charts.fitness.tsb')}
              fill="var(--chart-positive)"
              fillOpacity={0.6}
              isAnimationActive={false}
            />
            <Bar
              dataKey="tsbDown"
              stackId="tsb"
              name={t('charts.fitness.tsb')}
              fill="var(--chart-negative)"
              fillOpacity={0.6}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="ctl"
              name={t('charts.fitness.ctl')}
              stroke="var(--chart-ctl)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="atl"
              name={t('charts.fitness.atl')}
              stroke="var(--chart-atl)"
              strokeDasharray={ATL_DASH}
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <Legend>
        <LegendItem color="var(--chart-ctl)" label={t('charts.fitness.ctl')} />
        <LegendItem color="var(--chart-atl)" dash={ATL_DASH} label={t('charts.fitness.atl')} />
        <LegendItem color="var(--chart-positive)" bar label={t('charts.fitness.formPositive')} />
        <LegendItem color="var(--chart-negative)" bar label={t('charts.fitness.formNegative')} />
      </Legend>
    </div>
  );
}

function FitnessTooltip({
  active,
  payload,
}: Readonly<{ active?: boolean; payload?: ReadonlyArray<{ payload?: FitnessPoint }> }>) {
  const { t, i18n } = useTranslation();
  const p = payload?.[0]?.payload;
  if (!active || !p) return null;
  return (
    <TooltipBox title={formatDayLong(p.date, i18n.language)}>
      <TooltipRow label={t('charts.fitness.ctl')} value={`${Math.round(p.ctl)}`} />
      <TooltipRow label={t('charts.fitness.atl')} value={`${Math.round(p.atl)}`} />
      <TooltipRow label={t('charts.fitness.tsb')} value={formatSigned(p.tsb)} />
      <TooltipRow label="TSS" value={`${Math.round(p.tss)}`} />
    </TooltipBox>
  );
}
